/**
 * CPIP share-scope bootstrap — populates __webpack_require__.S["default"] with
 * window.React/ReactDOM entries so MFE eager-init chains resolve. Root cause:
 * webpack#15554 emits no MF share registrations for externalized packages.
 * No-op in standalone (window.React undefined). Inspect: window.__CPIP_SHARE_INIT__
 *
 * Kept as .js intentionally — runtime patch for a webpack bug, not application
 * code. Interacts with untyped webpack internals (__webpack_share_scopes__) and
 * window globals that would require boilerplate type declarations without
 * runtime benefit.
 */

/* eslint-disable no-undef */
const hasWindow = typeof window !== "undefined";
const SHARE_SCOPE_KEY = "__CPIP_SHARE_SCOPE__";

const cpipShareInit = () => {
  if (!isCpipEnvironment()) return;
  const versions = getReactVersions();
  if (!versions) return;
  const entries = buildRegistrationEntries(versions);
  const { scope, restored } = writeEntriesToShareScope(entries);
  exposeDiagnosticMarker(scope, versions, restored);
};

const isCpipEnvironment = () =>
  hasWindow &&
  !!window.React &&
  !!window.ReactDOM &&
  typeof __webpack_share_scopes__ !== "undefined";

const getReactVersions = () => {
  const reactVersion = window.React?.version;
  const reactDomVersion = window.ReactDOM?.version;
  if (!reactVersion || !reactDomVersion) return null;
  return { reactVersion, reactDomVersion };
};

const buildRegistrationEntries = ({ reactVersion, reactDomVersion }) => {
  const react = window.React;
  const reactDom = window.ReactDOM;
  const reactEntry = { key: "react", version: reactVersion, mod: react };
  const reactDomEntry = { key: "react-dom", version: reactDomVersion, mod: reactDom };
  const reactDomClientEntry = reactDom.createRoot
    ? { key: "react-dom/client", version: reactDomVersion, mod: reactDom }
    : null;
  const reactJsxRuntimeEntry = react.jsx
    ? { key: "react/jsx-runtime", version: reactVersion, mod: react }
    : null;
  return [reactEntry, reactDomEntry, reactDomClientEntry, reactJsxRuntimeEntry].filter(
    (e) => e !== null,
  );
};

const writeEntriesToShareScope = (entries) => {
  // Reuse one share-scope object across re-executions so a re-entry doesn't
  // init surviving remotes with a fresh scope (P81-129790).
  const saved = hasWindow ? window[SHARE_SCOPE_KEY] : null;
  if (saved) {
    __webpack_share_scopes__.default = saved;
  } else {
    __webpack_share_scopes__.default ??= {};
  }
  const scope = __webpack_share_scopes__.default;
  if (hasWindow && !saved) window[SHARE_SCOPE_KEY] = scope;
  for (const { key, version, mod } of entries) {
    scope[key] ??= {};
    scope[key][version] = {
      loaded: 1,
      eager: 1,
      from: "cpip-host-bootstrap",
      get: () => () => mod,
    };
  }
  return { scope, restored: !!saved };
};

const exposeDiagnosticMarker = (scope, versions, restored) => {
  window.__CPIP_SHARE_INIT__ = {
    restoredScope: restored, // when true, registeredKeys is the accumulated scope
    registeredKeys: Object.keys(scope),
    reactVersion: versions.reactVersion,
    reactDomVersion: versions.reactDomVersion,
    timestamp: Date.now(),
  };
};

cpipShareInit();
